import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewEncapsulation, inject } from '@angular/core';

import { DsBottomNavModule } from '@frontend/ui/bottom-nav';
import { DsNotificationBubble } from '@frontend/ui/notification-bubble';
import { DsTooltipModule } from '@frontend/ui/tooltip';
import {
    DeviceService,
    DslService,
    HtmlNode,
    MenuContentItem,
    MenuDisplayMode,
    MenuItemsService,
    MenuSection,
    Page,
    toBoolean,
    trackByProp,
} from '@frontend/vanilla/core';
import {
    MenuItemBadgeDirective,
    MenuItemClickHandlerService,
    MenuItemComponent,
    MenuItemTextContentComponent,
} from '@frontend/vanilla/features/menus';
import { DslPipe, TrustAsHtmlPipe } from '@frontend/vanilla/shared/browser';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { ImageComponent } from '@frontend/vanilla/shared/image';
import { Observable, Subject, combineLatest, of } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { BottomNavConfig } from './bottom-nav.client-config';
import { BottomNavService } from './bottom-nav.service';

/**
 * @stable
 */
@Component({
    imports: [
        CommonModule,
        DslPipe,
        MenuItemComponent,
        DsBottomNavModule,
        IconCustomComponent,
        ImageComponent,
        DsNotificationBubble,
        MenuItemBadgeDirective,
        MenuItemTextContentComponent,
        DsTooltipModule,
        TrustAsHtmlPipe,
    ],
    selector: 'vn-bottom-nav',
    templateUrl: 'bottom-nav.html',
    styleUrls: ['../../../../../themepark/themes/whitelabel/components/bottom-nav/styles.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class BottomNavComponent implements OnInit, OnDestroy {
    readonly content = inject(BottomNavConfig);
    readonly page = inject(Page);
    readonly bottomNavService = inject(BottomNavService);
    private readonly htmlNode = inject(HtmlNode);
    private readonly dslService = inject(DslService);
    private readonly menuItemClickHandler = inject(MenuItemClickHandlerService);
    private readonly deviceService = inject(DeviceService);
    private readonly menuItemsService = inject(MenuItemsService);

    readonly MenuSection = MenuSection;
    readonly trackByProp = trackByProp<MenuContentItem>('name');
    visible: boolean;
    lazyLoad: boolean;
    private readonly unsubscribe = new Subject<void>();

    ngOnInit() {
        combineLatest([this.dslService.evaluateExpression<boolean>(this.content.isEnabledCondition), this.bottomNavService.inputEvents])
            .pipe(takeUntil(this.unsubscribe))
            .subscribe((e: any[]) => {
                const enabled = e[0];
                const show = e[1].state === 'show';
                this.visible = enabled && show;
                this.lazyLoad = !this.deviceService.isRobot;
                this.htmlNode.setCssClass('bottom-nav-shown', this.visible);
            });
    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
        this.htmlNode.setCssClass('bottom-nav-shown', false);
    }

    getDisplayMode(displayMode?: string): MenuDisplayMode {
        return displayMode ? (displayMode as MenuDisplayMode) : MenuDisplayMode.Icon;
    }

    getIconName(item: MenuContentItem) {
        return item.parameters.iconName;
    }
    getIconName2(item: MenuContentItem) {
        return item.parameters.iconName2;
    }

    click(event: Event, item: MenuContentItem) {
        this.menuItemClickHandler.handleMenuTrack(item);
        this.menuItemClickHandler.handleMenuAction(event, item, MenuSection.BottomNav, true);
    }

    renderHtmlText(item: MenuContentItem): boolean | undefined {
        return toBoolean(item.parameters['render-html']);
    }

    isActive(item: MenuContentItem): Observable<boolean> {
        if (this.menuItemsService.isActive(MenuSection.BottomNav, item.name)) {
            return of(true);
        }

        if (item.parameters?.highlighted) {
            return this.dslService.evaluateExpression<boolean>(item.parameters.highlighted);
        }

        return of(false);
    }
}
